import { z } from "zod";

/**
 * Owners shared contract (Strangler-Fig migration, manifest-6).
 *
 * Single source of truth for the `owners` wire shape shared by the web SPA
 * (`src/repositories/owners.ts`) and the NestJS slice (`apps/api/src/owners/*`).
 * Shape is **snake_case** so the frontend<->Nest transport is a field-for-field
 * swap (no renames), matching the Supabase generated `owners` Row and the Nest
 * repository `.select('*')`.
 *
 * The web repo `.parse()`s Nest responses against {@link ownerSchema} at the
 * flip boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * ⚠️ TOLERANCE RULE (the #1699 silent-no-op lesson): a too-strict DTO makes
 * every Nest call silently fall back to Supabase. The owners staff endpoints
 * return the bare row (no joined relations), but the row carries optional
 * commercial-entity + representative columns added by later migrations, plus a
 * nullable `is_active`/`deleted_at`. To stay permissive we:
 *   - pin only the always-present columns (`id`, `company_id`, `full_name`),
 *   - make every other column `.optional()` + `.nullable()`,
 *   - `.passthrough()` so any new migration column never rejects the response.
 *
 * BE adoption note: `apps/api` does not yet import this package — it keeps its
 * class-validator DTO (already shape-aligned). Wiring the Nest side to consume
 * `@dallali/shared` needs the dual ESM/CJS build step tracked in
 * `docs/migration/domain-debugging-matrix.md`.
 */

/** Owner kinds (advisory — `owner_type` stays an open string to never reject server data). */
export const OWNER_TYPES = ["individual", "commercial"] as const;
export const ownerTypeSchema = z.enum(OWNER_TYPES);
export type OwnerType = (typeof OWNER_TYPES)[number];

/**
 * A persisted owner row as the staff dashboard consumes it. Own-columns field
 * set + nullability mirror the Supabase generated `owners` Row. Only the
 * always-present identity columns are pinned tightly; everything else is
 * optional + nullable so older rows (pre commercial-entity migration) and new
 * migration columns both parse.
 */
export const ownerSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    full_name: z.string(),
    full_name_en: z.string().nullable().optional(),
    national_id: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    date_of_birth: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    is_active: z.boolean().nullable().optional(),
    invitation_status: z.string().optional(),
    owner_type: z.string().optional(),
    user_id: z.string().nullable().optional(),
    // Commercial-entity + representative columns (migration 20260521120000).
    commercial_name: z.string().nullable().optional(),
    commercial_name_en: z.string().nullable().optional(),
    unified_number: z.string().nullable().optional(),
    cr_number: z.string().nullable().optional(),
    // Owner VAT registration number (الرقم الضريبي) — migration 20260623120000.
    vat_number: z.string().nullable().optional(),
    representative_name: z.string().nullable().optional(),
    representative_name_en: z.string().nullable().optional(),
    representative_id_number: z.string().nullable().optional(),
    representative_phone: z.string().nullable().optional(),
    deleted_at: z.string().nullable().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  // Tolerate extra columns the server may add (new migration columns) without
  // forcing a same-PR schema bump — keeps the flip from silently falling back.
  .passthrough();

export type Owner = z.infer<typeof ownerSchema>;
